import { UI, HStack, VStack } from '@genial/components';
import { BoxRadius, Spacing, BoxBorder } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useEffect } from 'react';

import { OperationType } from '../../../../../../../../../components/OperationType';
import { TableContentItemText } from '../../../../../../../../../components/TableContent';
import { useEquityAssetController } from '../../../../../hooks/products/Equity';
import { AssetTitleWithImage } from '../../../components/AssetTitleWithImage';
import { AssetValueWithPercentage } from '../../../components/AssetValueWithPercentage';
import { LateralBar } from '../../../components/LateralBar';
import {
  EQUITY_ORDER_TYPE_LABEL,
  EQUITY_ORDER_TYPE_OPTIONS,
  EquityListItemTypes,
} from '../ListItem';

import { EquityItemSelectedTypes } from '.';

export const EquityItemSelectedMobile: React.FC<EquityItemSelectedTypes.Props> = ({
  offer,
  onUpdate,
  hideLateralBar = false,
  isBatchTicket = true,
}) => {
  const id = offer.productIdNormalized;
  const {
    amountItem,
    priceItem,
    totalPrice,
    handleChangeAmountItem,
    handleChangePriceItem,
    totalPriceIsInvalid,
    orderType,
    handleChangeOrderType,
  } = useEquityAssetController(offer);

  useEffect(() => {
    if (offer.orderValue === priceItem || !priceItem) {
      return;
    }
    const updatedOffer = { ...offer, orderValue: priceItem || 0 };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceItem]);

  useEffect(() => {
    if (offer.orderQuantity === amountItem || !amountItem) {
      return;
    }
    const updatedOffer = { ...offer, orderQuantity: amountItem || 0 };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountItem]);

  useEffect(() => {
    if (isBatchTicket) {
      return;
    }
    const offerOrderType = offer.orderType ?? 'Limit';
    if (offerOrderType === orderType) {
      return;
    }
    const nextOrderValue =
      orderType === 'Market' ? Number((offer.quote.lastPrice ?? 0).toFixed(2)) : priceItem;
    onUpdate({
      ...offer,
      orderType,
      orderQuantity: amountItem,
      orderValue: nextOrderValue,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, isBatchTicket]);

  const middleInfos = [
    {
      label: 'Operação',
      value: <OperationType title="Compra" />,
    },
    {
      label: 'Validade da ordem',
      value: <UI.Text color="neutral.dark">Hoje</UI.Text>,
    },
    {
      label: 'Tipo da ordem',
      value: isBatchTicket ? (
        <UI.Text color="neutral.dark">Limitada</UI.Text>
      ) : (
        <VStack borderRadius={BoxBorder.thick} bgColor="backgroundshade" w="full">
          <UI.Select
            testID={`equity-order-type-select-conference-${id}`}
            options={EQUITY_ORDER_TYPE_OPTIONS}
            selectedValue={orderType}
            onValueChange={(value) => {
              handleChangeOrderType(value as EquityListItemTypes.OrderType);
            }}
            placeholder={EQUITY_ORDER_TYPE_LABEL.Limit}
          />
        </VStack>
      ),
    },
  ];

  return (
    <VStack space="stack.xxxs" w="full">
      {!hideLateralBar && (
        <LateralBar
          backgroundColor="product.rendavariavelbuy"
          borderTopLeftRadius={BoxRadius.sm}
          borderBottomLeftRadius={BoxRadius.sm}
        />
      )}
      <HStack justifyContent="space-between" flex={1} alignItems="center" py="stack.xxxs">
        <AssetTitleWithImage
          image={offer.imageNormalized || ''}
          title={offer.productNameNormalized || ''}
          subtitle={offer.symbolNormalized || ''}
        />
        <AssetValueWithPercentage
          value={offer.lastPriceNormalized}
          percentage={offer.priceChangeTaxValueNormalized}
          iconColor={offer.priceChangeTaxColorNormalized}
          updateDate={offer.tradeDateNormalized}
        />
      </HStack>
      <VStack space="stack.nano" flexGrow={1}>
        {middleInfos.map(({ label, value }) => (
          <HStack space="inline.quarck" justifyContent="space-between" key={label}>
            <UI.Text color="neutral.black" variant="semibold">
              {label}
            </UI.Text>
            {value}
          </HStack>
        ))}
      </VStack>

      <VStack space={Spacing['stack.quarck']} flexGrow={1}>
        <UI.Text color="neutral.black" variant="semibold">
          Quantidade
        </UI.Text>
        <VStack flex={1}>
          <UI.InputStepper
            stepperValue={offer.quote.lotSize || 1}
            max={1000000}
            min={offer.quote.lotSize || 1}
            name={`equity-amount-order-input${id}`}
            keyboardType="numeric"
            defaultValue={amountItem}
            onChange={handleChangeAmountItem}
            showErrorMessage={false}
            {...(totalPriceIsInvalid && {
              customError: 'Quantidade inválida',
            })}
          />
        </VStack>
      </VStack>

      <VStack space={Spacing['stack.quarck']} flexGrow={1}>
        <UI.Text color="neutral.black" variant="semibold">
          Preço por ativo
        </UI.Text>
        <VStack flex={1}>
          <UI.InputStepper
            stepperValue={0.01}
            max={1000000}
            min={0.01}
            keyboardType="numeric"
            ignoreStepperDivision
            maskOption="money"
            name={`equity-amount-price${id}`}
            defaultValue={priceItem || 0.01}
            onChange={handleChangePriceItem}
            showErrorMessage={false}
            disabled={!isBatchTicket && orderType === 'Market'}
            {...(totalPriceIsInvalid && {
              customError: 'Valor inválido',
            })}
          />
        </VStack>
      </VStack>

      <HStack space="inline.quarck" justifyContent="space-between">
        <UI.Text color="neutral.black" variant="semibold">
          Total estimado
        </UI.Text>
        <TableContentItemText>{Utils.Mask.Money(totalPrice)}</TableContentItemText>
      </HStack>
    </VStack>
  );
};
